import { KaraokeTrackManager } from '@src/editor/lib/KaraokeTrackManager';
import { PlacementReconciler } from '@src/editor/lib/PlacementReconciler';
import { SceneService } from '@src/services/SceneService';

import { PunchListTrackManager } from '../../lib/PunchListTrackManager';
import { VideoTranscriptElement } from '../../models/elements/VideoTranscriptElement';
import { AbstractCommand } from '../AbstractCommand';

import type { Element } from '../../models/Element';
import type { VideoEditor } from '../../VideoEditor';

export type ElementPlacement<T extends Element> = {
  element: T;
  time: number;
};

/**
 * Command to move an element to a new position on the timeline
 * Uses direct object references to elements and tracks
 */
export class ChangeElementPlacementsCommand extends AbstractCommand {
  private elementPlacements: ElementPlacement<Element>[];

  getName(): string {
    return 'ChangeElementPlacementsCommand';
  }

  /**
   * Creates a new ShiftElementCommand
   * @param editor The video editor instance
   * @param elementPlacements The elements to move
   */
  constructor(
    editor: VideoEditor,
    elementPlacements: ElementPlacement<Element>[],
  ) {
    super(editor);
    this.elementPlacements = elementPlacements;
  }

  execute(): boolean {
    const transcriptElementPlacements: ElementPlacement<VideoTranscriptElement>[] =
      this.elementPlacements.filter(
        (placement): placement is ElementPlacement<VideoTranscriptElement> =>
          placement.element instanceof VideoTranscriptElement,
      );
    const nonTranscriptElementPlacements = this.elementPlacements.filter(
      ({ element }) => !(element instanceof VideoTranscriptElement),
    );
    let result = true;

    // Verify that elements do not overlap on same track
    if (!this.validateNoOverlaps(nonTranscriptElementPlacements)) {
      return false;
    }

    if (transcriptElementPlacements.length > 0) {
      result =
        result &&
        this.changeTranscriptElementPlacements(transcriptElementPlacements);
    }
    if (nonTranscriptElementPlacements.length > 0) {
      result =
        result &&
        this.changeRegularElementPlacements(nonTranscriptElementPlacements);
    }
    if (result) {
      // Recalculate punch list position for punch list elements specifically
      const trackManager = this.editor.getTrackManager();
      const punchListElementIds = this.elementPlacements
        .filter(p => trackManager.isPunchListElement(p.element.id))
        .map(p => p.element.id);

      if (
        transcriptElementPlacements.length === 0 &&
        punchListElementIds.length > 0
      ) {
        const punchListTrackManager = new PunchListTrackManager(this.editor);
        punchListTrackManager.recalculatePunchListPosition(punchListElementIds);
        this.reconcileDependentTracks(false); // Reconcile karaoke and end cards, but skip punch list since we just recalculated it
        this.editor.timeline.recalculateDuration(); // Recalculate timeline duration after punch list position change

        return result;
      }

      // Recalculate timeline duration after successful move (includes reconciliation)
      this.recalculateTimeline();
    }

    return result;
  }

  override reconcileDependentTracks(punchlist: boolean = true): void {
    // Reconcile karaoke track if it exists
    const karaokeTrack = this.editor.getKaraokeTrack();
    if (karaokeTrack) {
      const karaokeTrackManager = new KaraokeTrackManager(this.editor);
      karaokeTrackManager.reconcileKaraokeTrack();
    }

    // Reconcile punch list items
    if (punchlist) {
      const punchListTrackManager = new PunchListTrackManager(this.editor);
      punchListTrackManager.reconcilePunchList();
    }

    // Reconcile pinned composition placements (PLAT-10921). Same position in
    // the order as in `AbstractCommand.reconcileDependentTracks` — third, after
    // karaoke and the punch list. See `PlacementReconciler.reconcile` (DS3).
    new PlacementReconciler(this.editor).reconcile();
  }

  /**
   * Validates that element placements do not create overlaps on the same track
   */
  private validateNoOverlaps(placements: ElementPlacement<Element>[]): boolean {
    if (placements.length === 0) return true;

    const epsilon = 0.001; // Small tolerance for floating-point comparisons

    // Group placements by track
    const placementsByTrack = new Map<
      string,
      Array<{ element: Element; time: number }>
    >();
    for (const placement of placements) {
      const trackId = this.editor.timeline.getTrackIdForElement(
        placement.element.id,
      );
      if (!trackId) return false;

      if (!placementsByTrack.has(trackId)) {
        placementsByTrack.set(trackId, []);
      }
      placementsByTrack.get(trackId)!.push(placement);
    }

    // Validate no overlaps on each track
    for (const [trackId, trackPlacements] of placementsByTrack.entries()) {
      const track = this.editor.timeline.getTrack(trackId);
      if (!track) return false;

      // Create a list of all elements on this track with their new or existing times
      const elementTimesMap = new Map<string, number>();
      const elementDurationsMap = new Map<string, number>();

      // Add all elements currently on the track
      for (const element of track.elements) {
        elementTimesMap.set(element.id, element.time);
        elementDurationsMap.set(element.id, element.durationSeconds);
      }

      // Update with new placements
      for (const { element, time } of trackPlacements) {
        elementTimesMap.set(element.id, time);
      }

      // Check for overlaps
      const elementPairs: Array<{
        id: string;
        time: number;
        endTime: number;
      }> = [];
      for (const [id, time] of elementTimesMap.entries()) {
        const duration = elementDurationsMap.get(id) ?? 0;
        elementPairs.push({ id, time, endTime: time + duration });
      }

      // Sort by time
      elementPairs.sort((a, b) => a.time - b.time);

      // Check adjacent elements for overlaps
      for (let i = 0; i < elementPairs.length - 1; i++) {
        const current = elementPairs[i];
        const next = elementPairs[i + 1];

        // Check if current element ends after next element starts (with small epsilon for floating point)
        if (current.endTime > next.time + epsilon) {
          console.warn(
            `Overlap detected on track ${trackId}: element ${current.id} (${current.time}-${current.endTime}) overlaps with ${next.id} (${next.time}-${next.endTime})`,
          );
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Shifts a VideoTranscriptElement using TranscriptTrackManager
   */
  private changeTranscriptElementPlacements(
    placements: ElementPlacement<VideoTranscriptElement>[],
  ): boolean {
    const transcriptTrack = this.editor.getVideoTranscriptTrack();
    if (!transcriptTrack) {
      return false;
    }
    const sceneService = new SceneService();
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();

    const sceneElementsSnapshots = sceneService.captureScenesForElements(
      this.editor.timeline,
      transcriptTrack.elements,
    );

    const result = transcriptTrackManager.changeElementPlacements(placements);
    if (result) {
      sceneService.restoreScenesFromSnapshots(
        this.editor.timeline,
        sceneElementsSnapshots,
      );
    }
    return result;
  }

  /**
   * Shifts a regular element using TrackManager
   */
  private changeRegularElementPlacements(
    placements: ElementPlacement<Element>[],
  ): boolean {
    const trackManager = this.editor.getTrackManager();
    return trackManager.changeElementPlacements(placements);
  }

  undo(): boolean {
    throw new Error('Method not implemented.');
  }

  getDescription(): string {
    return `Move elements`;
  }
}
